import { useCallback, useContext, useEffect, useState } from "react";
import { AppContext } from "../../../context/AppContext";
import useHttp from "../../../hooks/useHttp";
import type { ServerWorkflow } from "../parser/types";

export interface UseWorkflowResult {
    workflow: ServerWorkflow | null;
    isLoading: boolean;
    error: Error | null;
    refetch: () => Promise<void>;
}

/**
 * Fetches a single workflow (including its graph) by workflowId.
 * Returns null while loading or when workflowId is null/empty.
 */
export function useWorkflow(workflowId: string | null | undefined): UseWorkflowResult {
    const [appProperties] = useContext(AppContext);
    const { fetchData } = useHttp();
    const [workflow, setWorkflow] = useState<ServerWorkflow | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const loadWorkflow = useCallback(async () => {
        if (!workflowId || !appProperties) {
            setWorkflow(null);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await fetchData(`workflows/${workflowId}`, "GET", null, appProperties);
            if (!response) {
                setWorkflow(null);
                return;
            }

            const parsed = typeof response === "string" ? JSON.parse(response) : response;
            const payload: ServerWorkflow = parsed?.data ?? parsed;

            if (payload?.graph) {
                setWorkflow(payload);
            } else {
                setWorkflow(null);
            }
        } catch (e) {
            setError(e instanceof Error ? e : new Error(String(e)));
            setWorkflow(null);
        } finally {
            setIsLoading(false);
        }
    }, [workflowId, appProperties, fetchData]);

    useEffect(() => {
        loadWorkflow();
    }, [loadWorkflow]);

    return { workflow, isLoading, error, refetch: loadWorkflow };
}
